import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Col,
  Input,
  InputGroup,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
} from "reactstrap";
import { ApiError, requestJson } from "../../../helpers/api";
import type {
  CatalogoArticulo,
  CatalogoDeposito,
  CatalogoPunto,
  CatalogoSucursal,
  CatalogosState,
  MovimientoDetalle,
} from "./MovimientoBase.types";
import type { PlantillaContextForm } from "./MovimientoPlantillasModal";

interface MovimientoPlantillasCrearModalProps {
  visible: boolean;
  requiereTalonario: boolean;
  guardando: boolean;
  error: string | null;
  datos: {
    nombre: string;
    descripcion: string;
  };
  plantillaDetalles: MovimientoDetalle[];
  contextoForm: PlantillaContextForm;
  catalogos: CatalogosState;
  plantillaCatalogos: {
    depositos: CatalogoDeposito[];
    puntos: CatalogoPunto[];
  };
  empresaId: number;
  contextoDescripcion: string;
  onChange: (campo: keyof MovimientoPlantillasCrearModalProps["datos"], valor: string) => void;
  onContextChange: (campo: keyof PlantillaContextForm, valor: string) => void;
  onPlantillaDetalleChange: (id: number, campo: keyof MovimientoDetalle, valor: string) => void;
  onPlantillaDetalleAdd: () => void;
  onPlantillaDetalleRemove: (id: number) => void;
  onPlantillaDetalleMove: (id: number, direccion: "up" | "down") => void;
  onClose: () => void;
  onGuardar: () => void;
}

const MovimientoPlantillasCrearModal: React.FC<MovimientoPlantillasCrearModalProps> = ({
  visible,
  requiereTalonario,
  guardando,
  error,
  datos,
  plantillaDetalles,
  contextoForm,
  catalogos,
  plantillaCatalogos,
  empresaId,
  contextoDescripcion,
  onChange,
  onContextChange,
  onPlantillaDetalleChange,
  onPlantillaDetalleAdd,
  onPlantillaDetalleRemove,
  onPlantillaDetalleMove,
  onClose,
  onGuardar,
}) => {
  const [buscadorAbierto, setBuscadorAbierto] = useState(false);
  const [detalleBuscadoId, setDetalleBuscadoId] = useState<number | null>(null);
  const [busquedaArticulo, setBusquedaArticulo] = useState("");
  const [articulosResultados, setArticulosResultados] = useState<CatalogoArticulo[]>(catalogos.articulos);
  const [articulosCargando, setArticulosCargando] = useState(false);
  const [articulosError, setArticulosError] = useState<string | null>(null);
  const [ordenarPorGrupo, setOrdenarPorGrupo] = useState(false);
  const [forzarOrdenGrupo, setForzarOrdenGrupo] = useState(false);
  const [grupoModalAbierto, setGrupoModalAbierto] = useState(false);
  const [ordenGrupos, setOrdenGrupos] = useState<string[]>([]);

  useEffect(() => {
    setArticulosResultados(catalogos.articulos);
  }, [catalogos.articulos]);

  const cargarArticulos = async (termino = "") => {
    setArticulosCargando(true);
    setArticulosError(null);
    try {
      const params = new URLSearchParams({ empresaId: String(empresaId) });
      if (termino.trim()) {
        params.set("search", termino.trim());
      }
      const data = await requestJson<CatalogoArticulo[]>(`/api/stock/catalogos/articulos?${params}`);
      setArticulosResultados(data);
    } catch (error) {
      setArticulosError(
        error instanceof ApiError
          ? error.message
          : "No pudimos buscar artículos. Intentá nuevamente.",
      );
    } finally {
      setArticulosCargando(false);
    }
  };

  const abrirBuscadorArticulo = (detalleId: number) => {
    setDetalleBuscadoId(detalleId);
    setBusquedaArticulo("");
    setBuscadorAbierto(true);
    void cargarArticulos("");
  };

  const cerrarBuscadorArticulo = () => {
    setBuscadorAbierto(false);
    setDetalleBuscadoId(null);
    setBusquedaArticulo("");
    setArticulosError(null);
  };

  const seleccionarArticulo = (articulo: CatalogoArticulo) => {
    if (!detalleBuscadoId) return;
    const codigo = articulo.sku?.trim() || String(articulo.id);
    const yaExiste = plantillaDetalles.some(
      (detalle) =>
        detalle.id !== detalleBuscadoId &&
        detalle.codigo.trim().toUpperCase() === codigo.toUpperCase(),
    );
    if (yaExiste) {
      setArticulosError("El artículo ya fue agregado a la plantilla.");
      return;
    }
    onPlantillaDetalleChange(detalleBuscadoId, "codigo", codigo);
    onPlantillaDetalleChange(
      detalleBuscadoId,
      "descripcion",
      articulo.descripcion ?? "",
    );
    const unidadValor = articulo.sku?.trim() || "";
    if (unidadValor) {
      onPlantillaDetalleChange(detalleBuscadoId, "unidad", unidadValor);
    }
    const grupoValor =
      (articulo.grupoNombre ?? articulo.grupoCodigo ?? articulo.tipoItem)?.trim() || "";
    if (grupoValor) {
      onPlantillaDetalleChange(detalleBuscadoId, "grupo", grupoValor);
      onPlantillaDetalleChange(
        detalleBuscadoId,
        "grupoId",
        articulo.grupoId != null ? String(articulo.grupoId) : "",
      );
    }
    setArticulosError(null);
    cerrarBuscadorArticulo();
  };

  const detallesConDatos = plantillaDetalles.filter(
    (detalle) =>
      detalle.grupo?.trim() ||
      detalle.codigo.trim() !== "" ||
      detalle.descripcion.trim() !== "" ||
      detalle.depositoSalida.trim() !== "" ||
      detalle.depositoEntrada.trim() !== "" ||
      detalle.unidad.trim() !== "",
  );

  const getGrupoKey = (detalle: MovimientoDetalle) =>
    (detalle.grupo ?? "Sin grupo").trim() || "Sin grupo";

  const groupedDetails = useMemo(() => {
    const agrupados = new Map<string, MovimientoDetalle[]>();
    plantillaDetalles.forEach((detalle) => {
      const clave = getGrupoKey(detalle);
      if (!agrupados.has(clave)) {
        agrupados.set(clave, []);
      }
      agrupados.get(clave)!.push(detalle);
    });
    return Array.from(agrupados.entries());
  }, [plantillaDetalles]);

  const orderedGroupedDetails = useMemo(() => {
    if (!ordenarPorGrupo) return groupedDetails;
    if (forzarOrdenGrupo && ordenGrupos.length > 0) {
      const agrupados = new Map(groupedDetails);
      const ordenados: [string, MovimientoDetalle[]][] = [];
      ordenGrupos.forEach((grupo) => {
        if (agrupados.has(grupo)) {
          ordenados.push([grupo, agrupados.get(grupo)!]);
          agrupados.delete(grupo);
        }
      });
      agrupados.forEach((detalles, grupo) => {
        ordenados.push([grupo, detalles]);
      });
      return ordenados;
    }
    return groupedDetails;
  }, [groupedDetails, ordenarPorGrupo, forzarOrdenGrupo, ordenGrupos]);

  const gruposDisponibles = groupedDetails.map(([grupo]) => grupo);
  const tieneTresGrupos = gruposDisponibles.length >= 3;

  const swapOrdenGrupos = (index: number, direccion: "up" | "down") => {
    setOrdenGrupos((prev) => {
      const target = direccion === "up" ? index - 1 : index + 1;
      if (target < 0 || target >= prev.length) return prev;
      const nuevo = [...prev];
      [nuevo[index], nuevo[target]] = [nuevo[target], nuevo[index]];
      return nuevo;
    });
  };

  const abrirModalOrden = () => {
    if (!tieneTresGrupos) {
      setArticulosError("Necesitás al menos tres grupos para forzar un orden.");
      return;
    }
    setGrupoModalAbierto(true);
  };

  const cerrarModalOrden = () => {
    setGrupoModalAbierto(false);
  };

  useEffect(() => {
    const gruposActuales = groupedDetails.map(([grupo]) => grupo);
    if (forzarOrdenGrupo) {
      setOrdenGrupos((prev) =>
        prev.length === gruposActuales.length && gruposActuales.every((g) => prev.includes(g))
          ? prev
          : gruposActuales,
      );
    } else {
      setOrdenGrupos(gruposActuales);
    }
  }, [groupedDetails, forzarOrdenGrupo]);

  const handleMoveClick = (detalle: MovimientoDetalle, direccion: "up" | "down") => {
    const index = plantillaDetalles.findIndex((item) => item.id === detalle.id);
    if (index === -1) return;
    const targetIndex = direccion === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= plantillaDetalles.length) {
      return;
    }
    if (ordenarPorGrupo || forzarOrdenGrupo) {
      const grupoActual = getGrupoKey(detalle);
      const grupoVecino = getGrupoKey(plantillaDetalles[targetIndex]);
      if (grupoActual !== grupoVecino) {
        setArticulosError(
          "Solo podés mover artículos dentro del mismo grupo cuando el orden está agrupado.",
        );
        return;
      }
    }
    onPlantillaDetalleMove(detalle.id, direccion);
  };

  const renderDetalleRow = (detalle: MovimientoDetalle, index: number) => (
    <tr key={detalle.id}>
      <td>
        <InputGroup>
          <Input
            value={detalle.codigo}
            list="plantilla-articulos-options"
            onChange={(event) =>
              onPlantillaDetalleChange(detalle.id, "codigo", event.target.value)
            }
            placeholder="Código"
          />
          <Button
            color="soft-primary"
            type="button"
            onClick={() => abrirBuscadorArticulo(detalle.id)}
          >
            <i className="ri-search-line" />
          </Button>
        </InputGroup>
      </td>
      <td>
        {detalle.grupo ? (
          <Badge color="secondary" className="text-uppercase">
            {detalle.grupo}
          </Badge>
        ) : (
          <span className="text-muted small">Sin grupo</span>
        )}
      </td>
      <td>
        <Input
          value={detalle.unidad}
          readOnly
          className="bg-light text-muted"
          placeholder="Unidad"
        />
      </td>
      <td>
        <Input
          value={detalle.descripcion}
          readOnly
          className="bg-light text-muted"
          placeholder="Descripción artículo"
        />
      </td>
      <td className="text-end">
        <div className="d-flex gap-1 justify-content-end">
          <Button
            color="soft-secondary"
            size="sm"
            disabled={index === 0}
            onClick={() => handleMoveClick(detalle, "up")}
          >
            <i className="ri-arrow-up-line" />
          </Button>
          <Button
            color="soft-secondary"
            size="sm"
            disabled={index === plantillaDetalles.length - 1}
            onClick={() => handleMoveClick(detalle, "down")}
          >
            <i className="ri-arrow-down-line" />
          </Button>
          <Button
            color="soft-danger"
            size="sm"
            disabled={plantillaDetalles.length === 1}
            onClick={() => onPlantillaDetalleRemove(detalle.id)}
          >
            <i className="ri-delete-bin-6-line align-middle" />
          </Button>
        </div>
      </td>
    </tr>
  );

  return (
    <>
      <Modal isOpen={visible} toggle={onClose} size="xl">
        <ModalHeader toggle={onClose}>Crear plantilla</ModalHeader>
        <ModalBody>
          <p className="text-muted small mb-3">{contextoDescripcion}</p>
          <Alert color="info" className="mb-4">
            Las plantillas guardan los artículos sin cantidades cargadas para que las completes cada vez que la uses.
          </Alert>
          {error && (
            <Alert color="danger" className="mb-4">
              {error}
            </Alert>
          )}
          <div className="border rounded-3 p-3 mb-4">
            <h6 className="mb-3">Contexto de la plantilla</h6>
            <Row className="g-3 align-items-end">
              {requiereTalonario && (
                <>
                  <Col md={2}>
                    <Label className="form-label fw-semibold">Sucursal / Establecimiento *</Label>
                    <Input
                      type="select"
                      value={contextoForm.sucursalId}
                      onChange={(event) => onContextChange("sucursalId", event.target.value)}
                    >
                      <option value="">Seleccioná...</option>
                      {catalogos.sucursales.map((sucursal) => (
                        <option key={sucursal.id} value={String(sucursal.id)}>
                          {sucursal.fuente}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col md={2}>
                    <Label className="form-label fw-semibold">Depósito *</Label>
                    <Input
                      type="select"
                      value={contextoForm.depositoId}
                      onChange={(event) => onContextChange("depositoId", event.target.value)}
                      disabled={!contextoForm.sucursalId}
                    >
                      <option value="">Seleccioná...</option>
                      {plantillaCatalogos.depositos.map((deposito) => (
                        <option key={deposito.id} value={String(deposito.id)}>
                          {deposito.fuente}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col md={2}>
                    <Label className="form-label fw-semibold">Punto / Caja *</Label>
                    <Input
                      type="select"
                      value={contextoForm.puntoId}
                      onChange={(event) => onContextChange("puntoId", event.target.value)}
                      disabled={!contextoForm.depositoId || !contextoForm.sucursalId}
                    >
                      <option value="">Seleccioná...</option>
                      {plantillaCatalogos.puntos.map((punto) => (
                        <option key={punto.id} value={String(punto.id)}>
                          {punto.fuente}
                          {punto.codigoPunto ? ` · ${punto.codigoPunto}` : ""}
                        </option>
                      ))}
                    </Input>
                  </Col>
                </>
              )}
              {!requiereTalonario && (
                <Col md={3}>
                  <Label className="form-label fw-semibold">Depósito *</Label>
                  <Input
                    type="select"
                    value={contextoForm.depositoId}
                    onChange={(event) => onContextChange("depositoId", event.target.value)}
                  >
                    <option value="">Seleccioná...</option>
                    {plantillaCatalogos.depositos.map((deposito) => (
                      <option key={deposito.id} value={String(deposito.id)}>
                        {deposito.fuente}
                      </option>
                    ))}
                  </Input>
                </Col>
              )}
              <Col md={3}>
                <Label className="form-label fw-semibold">Nombre de la plantilla *</Label>
                <Input
                  value={datos.nombre}
                  onChange={(event) => onChange("nombre", event.target.value)}
                  placeholder="Ej. Plantilla reparto centro"
                />
              </Col>
              <Col md={3}>
                <Label className="form-label fw-semibold">Descripción</Label>
                <Input
                  value={datos.descripcion}
                  onChange={(event) => onChange("descripcion", event.target.value)}
                  placeholder="Anotaciones internas para identificarla"
                />
              </Col>
            </Row>
          </div>
          <div className="border rounded-3">
            <div className="px-3 py-2 border-bottom">
              <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                <span className="fw-semibold">Artículos incluidos</span>
                <span className="text-muted small">
                  Salida y entrada por depósito, junto con los artículos movidos.
                </span>
              </div>
              <div className="text-muted small">
                {detallesConDatos.length} artículo{detallesConDatos.length === 1 ? "" : "s"}
              </div>
            </div>
            <div className="px-3 py-2">
              <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
                <Button
                  size="sm"
                  color={ordenarPorGrupo ? "primary" : "secondary"}
                  outline={!ordenarPorGrupo}
                  onClick={() => setOrdenarPorGrupo((prev) => !prev)}
                >
                  {ordenarPorGrupo ? "Ver sin agrupar" : "Agrupar por grupo"}
                </Button>
                <Button
                  size="sm"
                  color="secondary"
                  outline
                  onClick={abrirModalOrden}
                  disabled={!tieneTresGrupos}
                >
                  Forzar orden de grupo
                </Button>
                {ordenarPorGrupo && (
                  <Badge color="info" className="text-uppercase">
                    Agrupado
                  </Badge>
                )}
                {forzarOrdenGrupo && (
                  <>
                    <Badge color="warning" className="text-uppercase">
                      Forzado
                    </Badge>
                    <Button
                      size="sm"
                      color="danger"
                      outline
                      onClick={() => setForzarOrdenGrupo(false)}
                    >
                      Desactivar forzado
                    </Button>
                  </>
                )}
              </div>
            </div>
            <div className="table-responsive">
              <Table className="align-middle table-sm mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Código</th>
                    <th>Grupo</th>
                    <th>Unidad</th>
                    <th>Descripción artículo</th>
                    <th style={{ width: "130px" }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {ordenarPorGrupo
                    ? orderedGroupedDetails.map(([grupo, detalles]) => (
                        <React.Fragment key={grupo}>
                          <tr className="border-bottom">
                            <td colSpan={5} className="fw-semibold">
                              {grupo}
                            </td>
                          </tr>
                          {detalles.map((detalle) => {
                            const index = plantillaDetalles.findIndex((item) => item.id === detalle.id);
                            return renderDetalleRow(detalle, index);
                          })}
                        </React.Fragment>
                      ))
                    : plantillaDetalles.map((detalle, index) => renderDetalleRow(detalle, index))}
                </tbody>
              </Table>
            </div>
            <div className="px-3 py-3 border-top d-flex flex-wrap gap-2 justify-content-between align-items-center">
              <span className="text-muted small">
                Podés agregar o ajustar la combinación de depósitos y artículos sin necesidad de completar el formulario principal.
              </span>
              <Button color="soft-primary" size="sm" onClick={onPlantillaDetalleAdd}>
                <i className="ri-add-line align-middle me-1" />
                Agregar artículo
              </Button>
            </div>
          </div>
          {catalogos.articulos.length > 0 && (
            <datalist id="plantilla-articulos-options">
              {catalogos.articulos.map((articulo) => (
                <option key={articulo.id} value={articulo.sku ?? String(articulo.id)}>
                  {articulo.descripcion}
                </option>
              ))}
            </datalist>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            color="primary"
            onClick={onGuardar}
            disabled={guardando || detallesConDatos.length === 0}
          >
            {guardando ? "Guardando..." : "Guardar plantilla"}
          </Button>
        </ModalFooter>
      </Modal>
      <Modal isOpen={buscadorAbierto} toggle={cerrarBuscadorArticulo} size="lg">
        <ModalHeader toggle={cerrarBuscadorArticulo}>Seleccionar artículo</ModalHeader>
        <ModalBody>
          <div className="d-flex gap-2 mb-3">
            <Input
              value={busquedaArticulo}
              onChange={(event) => setBusquedaArticulo(event.target.value)}
              placeholder="Buscar código o descripción"
            />
            <Button
              color="soft-primary"
              onClick={() => cargarArticulos(busquedaArticulo)}
              disabled={articulosCargando}
            >
              <i className="ri-search-line align-middle me-1" />
              Buscar
            </Button>
          </div>
          {articulosError && <Alert color="danger">{articulosError}</Alert>}
          {articulosCargando ? (
            <div className="text-center text-muted py-4">Buscando artículos...</div>
          ) : (
            <div className="table-responsive">
              <Table className="table-sm mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Código</th>
                    <th>Grupo</th>
                    <th>Descripción</th>
                    <th style={{ width: "120px" }}>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {articulosResultados.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center text-muted py-3">
                        No encontramos artículos para ese término.
                      </td>
                    </tr>
                  ) : (
                    articulosResultados.map((articulo) => (
                      <tr key={articulo.id}>
                        <td>{articulo.sku ?? String(articulo.id)}</td>
                        <td className="text-uppercase">
                          {(articulo.grupoNombre ?? articulo.grupoCodigo ?? articulo.tipoItem) || "-"}
                        </td>
                        <td>{articulo.descripcion}</td>
                        <td>
                          <Button
                            color="soft-success"
                            size="sm"
                            onClick={() => seleccionarArticulo(articulo)}
                          >
                            Seleccionar
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={cerrarBuscadorArticulo}>
            Cerrar
          </Button>
        </ModalFooter>
      </Modal>
      <Modal isOpen={grupoModalAbierto} toggle={cerrarModalOrden} size="md">
        <ModalHeader toggle={cerrarModalOrden}>Forzar orden de grupos</ModalHeader>
        <ModalBody>
          <p className="text-muted small mb-3">
            Reordená los grupos para que la tabla refleje el orden que definiste.
            Debés tener al menos tres grupos activos para aplicar un orden forzado.
          </p>
          <div className="table-responsive">
            <Table className="table-sm mb-0">
              <thead className="table-light">
                <tr>
                  <th>Grupo</th>
                  <th style={{ width: "140px" }}>Orden</th>
                </tr>
              </thead>
              <tbody>
                {ordenGrupos.map((grupo, index) => (
                  <tr key={grupo}>
                    <td>{grupo}</td>
                    <td>
                      <div className="d-flex gap-1 justify-content-end">
                        <Button
                          size="sm"
                          color="soft-secondary"
                          disabled={index === 0}
                          onClick={() => swapOrdenGrupos(index, "up")}
                        >
                          <i className="ri-arrow-up-line" />
                        </Button>
                        <Button
                          size="sm"
                          color="soft-secondary"
                          disabled={index === ordenGrupos.length - 1}
                          onClick={() => swapOrdenGrupos(index, "down")}
                        >
                          <i className="ri-arrow-down-line" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={cerrarModalOrden}>
            Cancelar
          </Button>
          <Button
            color="primary"
            disabled={ordenGrupos.length < 3}
            onClick={() => {
              setOrdenarPorGrupo(true);
              setForzarOrdenGrupo(true);
              cerrarModalOrden();
            }}
          >
            Guardar orden
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default MovimientoPlantillasCrearModal;
